import { HStack } from '@chakra-ui/react';

import {
  Button,
  Flex,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  TextInput,
  Tooltip,
  Typography,
} from 'shared';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';

import {
  SPRINT_OUTCOME_STATUS_COLOR_MAP,
  SPRINT_OUTCOME_STATUS_LABELS,
  SPRINT_OUTCOME_STATUSES,
} from 'features/planning/config';
import { SprintOutcome } from 'features/planning/types';

type SprintOutcomeItemProps = {
  id: string;
  name: string;
  status: SprintOutcome['status'];
  isEditing: boolean;
  editName: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  onEditNameChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onStatusChange?: (status: SprintOutcome['status']) => void;
};

export const SprintOutcomeItem = ({
  id,
  name,
  status,
  isEditing,
  editName,
  inputRef,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
  onDelete,
  onEditNameChange,
  onKeyDown,
  onStatusChange,
}: SprintOutcomeItemProps) => {
  const statusColor = SPRINT_OUTCOME_STATUS_COLOR_MAP[status];
  const statusLabel = SPRINT_OUTCOME_STATUS_LABELS[status];

  if (isEditing) {
    return (
      <Flex gap="0.25rem" align="center">
        <TextInput
          ref={inputRef}
          size="xs"
          value={editName}
          placeholder="Outcome name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onEditNameChange(e.target.value)
          }
          onKeyDown={onKeyDown}
          maxLength={80}
        />
        <Button
          size="xs"
          variant="solid"
          onClick={onSaveEdit}
          disabled={!editName.trim()}
        >
          Save
        </Button>
        <Button size="xs" variant="solid" onClick={onCancelEditing}>
          Cancel
        </Button>
      </Flex>
    );
  }

  return (
    <Flex align="center">
      <Flex
        borderRadius="var(--border-radius16)"
        h={8}
        px={2.5}
        border="var(--border-secondary)"
        alignItems="center"
        gap={3}
        cursor="pointer"
        _hover={{ borderColor: 'var(--border-secondary)' }}
        flex={1}
      >
        <Typography fontSize="xs">{name}</Typography>
        <MenuRoot size="sm">
          <Tooltip content={statusLabel} openDelay={200} closeDelay={0}>
            <MenuTrigger asChild>
              <Flex
                w={4}
                h={4}
                borderRadius="50%"
                bg={statusColor}
                flexShrink={0}
                cursor="pointer"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              />
            </MenuTrigger>
          </Tooltip>
          <MenuContent
            {...({
              portalled: false,
            } as any)}
          >
            {Object.values(SPRINT_OUTCOME_STATUSES).map((statusOption) => (
              // @ts-ignore
              <MenuItem
                key={statusOption}
                onClick={() => {
                  onStatusChange?.(statusOption);
                }}
              >
                <HStack>
                  <Flex
                    w={4}
                    h={4}
                    borderRadius="50%"
                    bg={SPRINT_OUTCOME_STATUS_COLOR_MAP[statusOption]}
                    flexShrink={0}
                  />
                  {SPRINT_OUTCOME_STATUS_LABELS[statusOption]}
                </HStack>
              </MenuItem>
            ))}
          </MenuContent>
        </MenuRoot>

        <Icon
          name={iconNames.pen}
          height="12px"
          width="12px"
          stroke="var(--fgPrimary)"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onStartEditing();
          }}
        />

        <Icon
          name={iconNames.cross}
          height="10px"
          width="10px"
          stroke="var(--fgPrimary)"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onDelete();
          }}
        />
      </Flex>
    </Flex>
  );
};
